/* Build in libraries */

/* Third party libraries */

import { useState } from 'react';
import BPWApprovedGrantAgreementsWidget from '@/components/Rover/Widgets/BPWApprovedGrantAgreementsWidget';
import TotalGrantsApprovedBPWWidget from '@/components/Rover/Widgets/BPWTotalGrantsApproved';
import ExecutedGrantAgreementsWidget from '@/components/Rover/Widgets/ExecutedGrantAgreementsWidget';
import ImpactByCountyWidget from '@/components/Rover/Widgets/ImpactByCountyWidget';
import TotalGranteeWidget from '@/components/Rover/Widgets/TotalGranteeWidget';
import TotalGrantsWidget from '@/components/Rover/Widgets/TotalGrantsWidget';
import TotalMatchedFundsWidget from '@/components/Rover/Widgets/TotalMatchedFundsWidget';
import UnExecutedGrantAgreementsWidget from '@/components/Rover/Widgets/UnExecutedGrantAgreementsWidget';
/* Components */
import Card from '@/components/UI/Card/index.tsx';
import DoughnutChart from '@/components/UI/Charts/DoughnutChart';
import { EApplicationStatus } from '@/features/ApplicationForm/constants';
/* Services */
import { convertEnumToText, isObjectEmpty } from '@/utils';

interface IApplicationDashboardComponentProps {
  data: any;
}
interface IDoughnutChart {
  dataLabels: string[];
  dataSets: { data: number; color: string }[];
}

export const MDGSDashboard = ({ data }: IApplicationDashboardComponentProps) => {
  const [viewStatus, setViewStatus] = useState('ALL');
  const applicationsList = data?.data;

  const widgetsData: {
    totalGrantsAmount: number;
    totalMatchedFunds: number;
    totalGrantee: number;
    prevailingWage: IDoughnutChart;
    approvedPrevailingWage: IDoughnutChart;
    approvedSatf: IDoughnutChart;
    approvedMHTEasements: IDoughnutChart;
    approvedUtilizingMBE: IDoughnutChart;
    satf: IDoughnutChart;
    MHTEasements: IDoughnutChart;
    utilizingMBE: IDoughnutChart;
    statusSummary: { [key: string]: number };
  } = {
    totalGrantsAmount: 0,
    totalMatchedFunds: 0,
    totalGrantee: 0,
    statusSummary: {},
    prevailingWage: {
      dataLabels: ['Prevailing Wage', 'No Prevailing Wage'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    satf: {
      dataLabels: ['SATF', 'No SATF'],
      dataSets: [
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#DC3545' },
      ],
    },
    MHTEasements: {
      dataLabels: ['MHT Easement', 'No MHT Easement'],
      dataSets: [
        { data: 0, color: '#808080' },
        { data: 0, color: '#C2CFE0' },
      ],
    },
    utilizingMBE: {
      dataLabels: ['MBE’s', 'No MBE’s'],
      dataSets: [
        { data: 0, color: '#334D6E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    approvedPrevailingWage: {
      dataLabels: ['Prevailing Wage', 'No Prevailing Wage'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    approvedSatf: {
      dataLabels: ['SATF', 'No SATF'],
      dataSets: [
        { data: 0, color: '#1F79B8' },
        { data: 0, color: '#DC3545' },
      ],
    },
    approvedMHTEasements: {
      dataLabels: ['MHT Easement', 'No MHT Easement'],
      dataSets: [
        { data: 0, color: '#808080' },
        { data: 0, color: '#C2CFE0' },
      ],
    },
    approvedUtilizingMBE: {
      dataLabels: ['MBE’s', 'No MBE’s'],
      dataSets: [
        { data: 0, color: '#334D6E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;
    //Total Grants Amount Widget
    widgetsData.totalGrantsAmount += applicationData?.step1?.section2?.grantAmount;

    //Total Matched Funds widget
    widgetsData.totalMatchedFunds += applicationData?.step1?.section2?.matchAmount;

    //Total Grantee widget
    widgetsData.totalGrantee += applicationData?.step1?.section2?.granteeOrganization ? 1 : 0;

    //Status Summary
    widgetsData.statusSummary[a.status] = (widgetsData.statusSummary[a.status] ?? 0) + 1;

    // Prevailing Wage
    if (applicationData?.step2?.q1 === 'yes') {
      widgetsData.prevailingWage.dataSets[0].data += 1;
    } else {
      widgetsData.prevailingWage.dataSets[1].data += 1;
    }

    // SATF Wage
    if (applicationData?.step2?.q2 === 'yes') {
      widgetsData.satf.dataSets[0].data += 1;
    } else {
      widgetsData.satf.dataSets[1].data += 1;
    }

    // MHT Easements
    if (applicationData?.step1?.section4?.requirePreservationEasement === 'yes') {
      widgetsData.MHTEasements.dataSets[0].data += 1;
    } else {
      widgetsData.MHTEasements.dataSets[1].data += 1;
    }

    // Utilizing MBE
    if (applicationData?.step1?.projectSchedule?.utilizeMBEEnterprise === 'yes') {
      widgetsData.utilizingMBE.dataSets[0].data += 1;
    } else {
      widgetsData.utilizingMBE.dataSets[1].data += 1;
    }

    if (a?.status === 'APPROVED') {
      // Prevailing Wage
      if (applicationData?.step2?.q1 === 'yes') {
        widgetsData.approvedPrevailingWage.dataSets[0].data += 1;
      } else {
        widgetsData.approvedPrevailingWage.dataSets[1].data += 1;
      }

      // SATF Wage
      if (applicationData?.step2?.q2 === 'yes') {
        widgetsData.approvedSatf.dataSets[0].data += 1;
      } else {
        widgetsData.approvedSatf.dataSets[1].data += 1;
      }

      // MHT Easements
      if (applicationData?.step1?.section4?.requirePreservationEasement === 'yes') {
        widgetsData.approvedMHTEasements.dataSets[0].data += 1;
      } else {
        widgetsData.approvedMHTEasements.dataSets[1].data += 1;
      }

      // Utilizing MBE
      if (applicationData?.step1?.projectSchedule?.utilizeMBEEnterprise === 'yes') {
        widgetsData.approvedUtilizingMBE.dataSets[0].data += 1;
      } else {
        widgetsData.approvedUtilizingMBE.dataSets[1].data += 1;
      }
    }
  });

  const convertIntoPercentage = (arrayOfData: { data: number; color: string }[]) => {
    const totalData = arrayOfData.reduce((acc: number, currentData: { data: number; color: string }) => {
      return acc + currentData.data;
    }, 0);
    const percentage = arrayOfData?.map((d: { data: number; color: string }) => {
      const convertNumberIntoPercentage = (d.data * 100) / totalData;
      const convertFloatToInteger = Math.ceil(convertNumberIntoPercentage);
      return {
        ...d,
        data: convertFloatToInteger ? convertFloatToInteger : 0,
      };
    });

    return percentage;
  };

  return (
    <div className='max-w-screen-3xl mx-auto'>
      <div className='font-semibold text-bold py-4 rounded-md space-x-2'>
        <span
          className={`${
            viewStatus === 'ALL' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
          } text-white px-4 py-2 cursor-pointer`}
          onClick={() => setViewStatus('ALL')}
        >
          All Applications
        </span>
        <span
          className={`${
            viewStatus === 'APPROVED' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
          } text-white px-4 py-2 cursor-pointer`}
          onClick={() => setViewStatus('APPROVED')}
        >
          Approved Applications
        </span>
      </div>
      {viewStatus === 'ALL' && (
        <>
          <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
            <UnExecutedGrantAgreementsWidget count={338} />
            <ExecutedGrantAgreementsWidget count={112} />
            <BPWApprovedGrantAgreementsWidget count={155} />
            <TotalGrantsWidget count={632661055} />
            <TotalGrantsApprovedBPWWidget count={19324342} />
            <TotalMatchedFundsWidget count={158165264} />
            <TotalGranteeWidget count={450} />
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4 my-4'>
            <Card title='Project Subject to Prevailing Wage'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.prevailingWage?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.prevailingWage?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Projects Subject to SATF'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.satf?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.satf?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Projects Requiring MHT Easements'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.MHTEasements?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.MHTEasements?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Projects Utilizing MBE’s'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.utilizingMBE?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.utilizingMBE?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className='my-4'>
            <Card title='Status Summary'>
              <div className='grid grid-cols-8 gap-6 py-6 px-6'>
                {!isObjectEmpty(EApplicationStatus) &&
                  Object.keys(EApplicationStatus).map((s: any) => (
                    <div key={s} className='col-span-2 font-medium flex justify-between gap-6'>
                      <span>{convertEnumToText(EApplicationStatus[s as keyof typeof EApplicationStatus])}</span>
                      <span>{widgetsData.statusSummary[s] ?? 0}</span>
                    </div>
                  ))}
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 my-4'>
            <ImpactByCountyWidget />
          </section>
          {/* <section className="my-4">
          <Card title="Project Location">
            {true && (
              <div className="w-full flex justify-center items-center h-screen/2">
                <div className="flex justify-center items-center gap-2 text-primaryColor-normal">
                  <MapPinIcon className="h-8 w-8" />
                  <h1>No Projects</h1>
                </div>
                <p>There are no assigned projects to display.</p>
              </div>
            )}
            {false && (
              <div className="w-full grid grid-cols-6 h-screen/2">
                <div className="col-span-6 w-full border-l p-5">
                  <MapGoogle projectList={widgetsData?.project_location} />
                </div>
              </div>
            )}
          </Card>
        </section> */}
        </>
      )}
      {viewStatus === 'APPROVED' && (
        <>
          <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
            <UnExecutedGrantAgreementsWidget count={104} />
            <ExecutedGrantAgreementsWidget count={60} />
            <BPWApprovedGrantAgreementsWidget count={58} />
            <TotalGrantsWidget count={156024681} />
            <TotalGrantsApprovedBPWWidget count={55179460} />
            <TotalMatchedFundsWidget count={49329483} />
            <TotalGranteeWidget count={164} />
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4 my-4'>
            <Card title='Project Subject to Prevailing Wage'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.prevailingWage?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.approvedPrevailingWage?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Projects Subject to SATF'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.satf?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.approvedSatf?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Projects Requiring MHT Easements'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.MHTEasements?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.approvedMHTEasements?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Projects Utilizing MBE’s'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-3/3 '>
                  <DoughnutChart
                    dataLabels={widgetsData?.utilizingMBE?.dataLabels}
                    datasets={convertIntoPercentage(widgetsData?.approvedUtilizingMBE?.dataSets)}
                    percentage={true}
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 my-4'>
            <ImpactByCountyWidget />
          </section>
          {/* <section className="my-4">
          <Card title="Project Location">
            {true && (
              <div className="w-full flex justify-center items-center h-screen/2">
                <div className="flex justify-center items-center gap-2 text-primaryColor-normal">
                  <MapPinIcon className="h-8 w-8" />
                  <h1>No Projects</h1>
                </div>
                <p>There are no assigned projects to display.</p>
              </div>
            )}
            {false && (
              <div className="w-full grid grid-cols-6 h-screen/2">
                <div className="col-span-6 w-full border-l p-5">
                  <MapGoogle projectList={widgetsData?.project_location} />
                </div>
              </div>
            )}
          </Card>
        </section> */}
        </>
      )}
    </div>
  );
};
